import React from "react";
import styled from "styled-components";
import { FiFileText, FiSearch } from "react-icons/fi";
import { colors } from "./colors";
import { formatDay, formatNumber, formatSignedPercent } from "./format";

/** Google updates and the days the crawl found page changes, newest first; a change day filters the drops. */
export default function EventsRail({ items, hoveredId, activeDate, onHover, onSelectDay }) {
    return (
        <Rail aria-labelledby="events-heading">
            <Heading id="events-heading">Google updates & page changes</Heading>
            <Note>Select a change day to filter drops · % is 7-day click avg vs. week before</Note>
            {items.length === 0 ? (
                <Empty>No Google updates or detected page changes in this period.</Empty>
            ) : (
                <List>
                    {items.map((item) => {
                        const isChange = item.type === "changes";
                        const color = isChange ? colors.pageChange : colors.algorithm;
                        const Icon = isChange ? FiFileText : FiSearch;
                        const significant = item.impact !== null && item.impact <= -5;
                        const content = (
                            <>
                                <IconBox $color={color} aria-hidden><Icon /></IconBox>
                                <Body>
                                    <Name>{isChange ? `${formatNumber(item.day.changeCount)} page changes` : item.event.title}</Name>
                                    <Meta>
                                        {formatDay(item.date)}
                                        {isChange && ` · ${[...new Set(item.day.entries.map((entry) => entry.field))].join(", ")}`}
                                    </Meta>
                                </Body>
                                <Impact $color={item.impact === null ? colors.subtle : significant ? colors.criticalInk : colors.muted}>
                                    {item.impact === null ? "—" : formatSignedPercent(item.impact)}
                                </Impact>
                            </>
                        );
                        return (
                            <li key={item.id} onMouseEnter={() => onHover(item.id)} onMouseLeave={() => onHover(null)}>
                                {isChange ? (
                                    <Item as="button" type="button" aria-pressed={activeDate === item.date}
                                          $active={activeDate === item.date} $hovered={hoveredId === item.id} $clickable
                                          onClick={() => onSelectDay(item.date)} onFocus={() => onHover(item.id)} onBlur={() => onHover(null)}>
                                        {content}
                                    </Item>
                                ) : (
                                    <Item $hovered={hoveredId === item.id}>{content}</Item>
                                )}
                            </li>
                        );
                    })}
                </List>
            )}
        </Rail>
    );
}

const Rail = styled.aside`
    padding: 20px;
    border-left: 1px solid ${colors.line};
    @media (max-width: 1100px) { border-left: none; border-top: 1px solid ${colors.line}; }
`;

const Heading = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.ink};
`;

const Note = styled.p`
    margin: 2px 0 0;
    font-size: 12px;
    color: ${colors.muted};
`;

const Empty = styled.p`
    margin: 24px 0 0;
    font-size: 14px;
    color: ${colors.muted};
`;

const List = styled.ul`
    max-height: 300px;
    overflow-y: auto;
    margin: 12px -8px 0;
    padding: 0 8px;
    list-style: none;
`;

const Item = styled.div`
    display: flex;
    gap: 12px;
    width: calc(100% + 16px);
    margin: 0 -8px 2px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    text-align: left;
    font: inherit;
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
    background: ${({ $active, $hovered }) => ($active ? colors.accentSoft : $hovered ? colors.canvas : "transparent")};
    box-shadow: ${({ $active }) => ($active ? `inset 0 0 0 1px ${colors.accent}4d` : "none")};
    transition: background 150ms;
`;

const IconBox = styled.span`
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    margin-top: 2px;
    flex-shrink: 0;
    border-radius: 6px;
    font-size: 14px;
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `${$color}14`};
`;

const Body = styled.span`
    flex: 1;
    min-width: 0;
`;

const Name = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
    color: ${colors.ink};
`;

const Meta = styled.span`
    display: block;
    margin-top: 2px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    color: ${colors.muted};
`;

const Impact = styled.span`
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ $color }) => $color};
`;
